/**
 * Desk — top-level page for users with existing 便签.
 *
 * Replaces the old SelectCards-inside-wish-flow model. The Desk shows everything
 * the user has on their table:
 *   - 未归档区:pending + in_progress 便签 (selectable, pick 1-3 to start)
 *   - 抽屉:done 便签 (always visible, read-only)
 *
 * Entrypoint: returning users land here directly. First-time users go through
 * WishInput first; after writing one wish, they come back to the Desk to pick
 * 1-3 notes to start.
 *
 * "写下愿望" is a persistent action — users can write a new wish at any time
 * and it gets translated into 便签 that join the Desk pool.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AuthUser } from '../types';
import type { ActiveSegment } from '../state/segment';
import {
  activePoolCards,
  archivedCards,
  commitSelection,
  fetchAllCards,
  updateCardStatus,
  type PoolCard,
} from '../state/cardPool';
import { apiPost } from '../api/client';
import { reportClientError } from '../telemetry';

interface DeskProps {
  user: AuthUser;
  onStartSegment: (selected: PoolCard[], segmentId: string | null) => void;
  onWriteWish: () => void;
  activeSegment?: ActiveSegment | null;
  onResumeSegment?: () => void;
  onLogout: () => void;
  /** Optional: when arriving from SegmentSummary, surface a quick re-entry banner. */
  recentlyCompletedSegment?: boolean;
}

interface SegmentStartResponse {
  ok: boolean;
  segmentId?: string;
  error?: string;
}

const MIN_PICK = 1;
const MAX_PICK = 3;
const DROP_UNDO_MS = 5000;
const DROP_PENDING_PREFIX = 'gime5.pendingDrop.v1';

function pendingDropKey(userId: string): string {
  return `${DROP_PENDING_PREFIX}:${userId}`;
}

function loadPendingDrop(userId: string): { card: PoolCard; until: number } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(pendingDropKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { card?: PoolCard; until?: number };
    if (!parsed.card || typeof parsed.until !== 'number') return null;
    return { card: parsed.card, until: parsed.until };
  } catch {
    localStorage.removeItem(pendingDropKey(userId));
    return null;
  }
}

function savePendingDrop(userId: string, pending: { card: PoolCard; until: number } | null): void {
  if (typeof window === 'undefined') return;
  if (!pending) {
    localStorage.removeItem(pendingDropKey(userId));
    return;
  }
  localStorage.setItem(pendingDropKey(userId), JSON.stringify(pending));
}

export function Desk({
  user,
  onStartSegment,
  onWriteWish,
  activeSegment,
  onResumeSegment,
  onLogout,
  recentlyCompletedSegment,
}: DeskProps) {
  const userId = user.id || user.email;
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectionHint, setSelectionHint] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Pending drop: one card at a time. While this is non-null the affected card
  // disappears from view (hidden via filter below), a toast counts down 5s and
  // offers "撤回". If the user does nothing for 5s we commit dropped to server.
  const [pendingDrop, setPendingDrop] = useState<{ card: PoolCard; until: number } | null>(() =>
    loadPendingDrop(userId),
  );
  const [dropRemainingMs, setDropRemainingMs] = useState(0);
  const dropTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchAllCards(userId).finally(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  const active = useMemo(() => {
    const all = activePoolCards(userId);
    // hide the card that's mid-undo so the user clearly perceives it as torn
    if (pendingDrop) return all.filter((c) => c.id !== pendingDrop.card.id);
    return all;
  }, [userId, loaded, reloadKey, pendingDrop]);
  const archive = useMemo(() => archivedCards(userId), [userId, loaded, reloadKey]);
  const drawerPreview = drawerOpen ? archive : archive.slice(0, 4);
  const drawerCanToggle = archive.length > 4;

  // Run the undo countdown when pendingDrop is set.
  useEffect(() => {
    if (!pendingDrop) {
      setDropRemainingMs(0);
      return;
    }
    const tick = () => {
      const remaining = pendingDrop.until - Date.now();
      if (remaining <= 0) {
        // commit drop: local pool + best-effort server sync
        updateCardStatus(userId, pendingDrop.card.id, 'dropped');
        void apiPost('/api/cards/drop', { cardId: pendingDrop.card.id }).catch((err) =>
          console.warn('[cards/drop] failed', err),
        );
        savePendingDrop(userId, null);
        setPendingDrop(null);
        setReloadKey((k) => k + 1); // re-read pool to refresh memos
        return;
      }
      setDropRemainingMs(remaining);
    };
    tick();
    dropTimerRef.current = window.setInterval(tick, 100);
    return () => {
      if (dropTimerRef.current !== null) {
        window.clearInterval(dropTimerRef.current);
        dropTimerRef.current = null;
      }
    };
  }, [pendingDrop, userId]);

  function requestDrop(card: PoolCard) {
    if (pendingDrop) return;
    // Take the card off selection so the count doesn't lie.
    setSelected((prev) => {
      if (!prev.has(card.id)) return prev;
      const next = new Set(prev);
      next.delete(card.id);
      return next;
    });
    const next = { card, until: Date.now() + DROP_UNDO_MS };
    savePendingDrop(userId, next);
    setPendingDrop(next);
  }

  function cancelDrop() {
    savePendingDrop(userId, null);
    setPendingDrop(null);
  }

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
      setSelectionHint(null);
      setSelected(next);
      return;
    }
    if (next.size >= MAX_PICK) {
      setSelectionHint(`最多只能选 ${MAX_PICK} 张卡片。先取消一张,再换另一张。`);
      return;
    }
    next.add(id);
    setSelectionHint(`已选 ${next.size}/${MAX_PICK} 张卡片`);
    setSelected(next);
  }

  async function confirm() {
    if (selected.size < MIN_PICK || committing) return;
    setCommitting(true);
    setError(null);
    const merged = commitSelection(userId, [], active, selected);
    const chosen = merged.filter((c) => selected.has(c.id) && c.status === 'in_progress');

    let segmentId: string | null = null;
    try {
      const candidates = active.map((c) => ({
        id: c.id,
        title: c.title,
        action: c.action,
      }));
      const res = await apiPost<SegmentStartResponse>('/api/segment/start', {
        wish: '',
        selectedCardIds: Array.from(selected),
        candidates,
      });
      if (res.ok && res.segmentId) segmentId = res.segmentId;
    } catch (err) {
      console.warn('[segment/start from desk] failed', err);
      reportClientError('write.segment_start', String(err), { context: 'from=desk' });
      setError('刚才没接上后台,这一段还是可以做,只是不会留底。');
    }
    setCommitting(false);
    onStartSegment(chosen, segmentId);
  }

  const isDevStub = user.email === 'dev@local.test';
  const totalVisible = active.length + archive.length;
  const isEmpty = loaded && totalVisible === 0;

  return (
    <section className="panel desk">
      <div className="user-strip">
        <span className="muted tiny">
          {isDevStub ? 'DEV 模式 · 已跳过登录' : `已登录 ${user.email}`}
        </span>
        {!isDevStub && (
          <button className="link-subtle" type="button" onClick={onLogout}>
            退出登录
          </button>
        )}
      </div>

      {recentlyCompletedSegment && (
        <div className="drafts-banner" role="status">
          <span className="muted tiny">
            上一段已经结束。桌面上的便签都在,挑一到三张可以接着开下一段。
          </span>
        </div>
      )}

      {activeSegment && onResumeSegment && (
        <div className="desk-resume-inline" role="status">
          <span className="tiny-label">
            <i className="dot lime" /> 工作区第 {activeSegment.round} / 8 轮
          </span>
          <button className="link-subtle" type="button" onClick={onResumeSegment}>
            回工作区
          </button>
        </div>
      )}

      <div className="desk-title-row">
        <div className="desk-title-copy">
          <span className="tiny-label">
            <i className="dot cyan" /> 桌面 · 共 {active.length} 张待启动
            {archive.length > 0 && <span className="muted">,{archive.length} 张已归档</span>}
          </span>
          <h1>今天想做哪几张?</h1>
          <p className="muted">
            随时可以写下新的愿望,它会被翻译成几张便签加到桌面上。归档的便签收在抽屉里,翻一翻就行。
          </p>
        </div>
        <button
          type="button"
          className="desk-pen"
          aria-label="写下新的愿望"
          onClick={onWriteWish}
        >
          <DeskWriteNoteIcon />
        </button>
      </div>

      {!loaded && (
        <p className="muted tiny">正在把桌面上的便签摆出来…</p>
      )}

      {isEmpty && (
        <div className="desk-empty">
          <p className="muted">
            桌面是空的。写下一个愿望,把它拆成几张便签放上来。
          </p>
          <button className="btn-lime" type="button" onClick={onWriteWish}>
            写下愿望
          </button>
        </div>
      )}

      {active.length > 0 && (
        <div className="group">
          <div className="group-head">
            <span className="tiny-label">
              <i className="dot lime" /> 桌面上的便签 · 已选 {selected.size}/{MAX_PICK} 张卡片
            </span>
          </div>
          <ul className="card-grid">
            {active.map((c) => (
              <DeskCardItem
                key={c.id}
                card={c}
                checked={selected.has(c.id)}
                onToggle={() => toggle(c.id)}
                onDrop={() => requestDrop(c)}
                dropDisabled={pendingDrop !== null}
              />
            ))}
          </ul>
        </div>
      )}

      {loaded && (
        <div className="group desk-drawer-group">
          <div className="group-head">
            <span className="tiny-label">
              <i className="dot amber" /> 抽屉 · 已归档 {archive.length} 张
            </span>
            {drawerCanToggle && (
              <button
                className="link-subtle desk-drawer-toggle"
                type="button"
                onClick={() => setDrawerOpen((open) => !open)}
              >
                {drawerOpen ? '收起' : `展开 ${archive.length - drawerPreview.length} 张`}
              </button>
            )}
          </div>
          {archive.length > 0 ? (
            <ul className="desk-drawer-list">
              {drawerPreview.map((c) => (
                <li key={c.id} className="desk-drawer-item">
                  <strong>{c.title}</strong>
                  <span className="muted tiny">{c.action}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="desk-drawer-empty muted tiny">完成的便签会收在这里。</p>
          )}
        </div>
      )}

      {!isEmpty && (
        <div className="actions desk-actions desk-actions-right">
          <span className="selection-counter" role="status" aria-live="polite">
            {selectionHint ?? `已选 ${selected.size}/${MAX_PICK} 张卡片`}
          </span>
          <button
            className="btn-lime"
            type="button"
            onClick={confirm}
            disabled={selected.size < MIN_PICK || committing}
          >
            {committing
              ? '正在放入工作区…'
              : selected.size === 0
                ? `已选 0/${MAX_PICK} 张卡片 · 放入工作区`
                : `已选 ${selected.size}/${MAX_PICK} 张卡片 · 放入工作区`}
          </button>
        </div>
      )}

      {error && (
        <p className="error-line">
          <i className="dot amber" /> {error}
        </p>
      )}

      {pendingDrop && (
        <DeskDropUndoToast
          card={pendingDrop.card}
          remainingMs={dropRemainingMs}
          onCancel={cancelDrop}
        />
      )}
    </section>
  );
}

interface DeskDropUndoToastProps {
  card: PoolCard;
  remainingMs: number;
  onCancel: () => void;
}

function DeskDropUndoToast({ card, remainingMs, onCancel }: DeskDropUndoToastProps) {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  return (
    <div className="drop-undo-toast desk-drop-undo-toast" role="status" aria-live="polite">
      <span>「{card.title}」撕了 · {seconds}s 内可撤回</span>
      <button type="button" className="link-subtle drop-undo-action" onClick={onCancel}>
        撤回
      </button>
    </div>
  );
}

function DeskWriteNoteIcon() {
  // Pencil + sticky note, kept as inline SVG so it stays crisp through hover
  // and disabled states.
  return (
    <svg viewBox="0 0 96 72" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="notePaper" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fffdf0" />
          <stop offset="1" stopColor="#f2f8d3" />
        </linearGradient>
        <linearGradient id="pencilWood" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f7c46f" />
          <stop offset="1" stopColor="#e49b35" />
        </linearGradient>
        <linearGradient id="pencilBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#32d5de" />
          <stop offset="1" stopColor="#16a9b4" />
        </linearGradient>
      </defs>
      <ellipse cx="48" cy="62" rx="34" ry="4.4" fill="rgba(20,24,28,.16)" />
      <g transform="rotate(-2 38 34)">
        <rect x="14" y="12" width="48" height="44" rx="5" fill="url(#notePaper)" stroke="#111" strokeWidth="1.4" />
        <rect x="25" y="7" width="26" height="9" rx="3" fill="rgba(255,255,255,.66)" stroke="rgba(17,17,17,.18)" />
        <path d="M24 28h28M24 38h21" stroke="rgba(17,17,17,.18)" strokeWidth="2" strokeLinecap="round" />
        <circle cx="54" cy="18" r="6" fill="#D8FF23" stroke="#111" strokeWidth="1.2" />
        <path d="M54 14.5v7M50.5 18h7" stroke="#111" strokeWidth="1.4" strokeLinecap="round" />
      </g>
      <g transform="rotate(-28 56 40)">
        <path d="M42 35l-12 4.5L42 44z" fill="url(#pencilWood)" stroke="#111" strokeWidth="1" />
        <path d="M32.8 38.4l-2.8 1.1 2.8 1.1z" fill="#111" />
        <rect x="42" y="35" width="35" height="9" rx="3" fill="url(#pencilBody)" stroke="#111" strokeWidth="1.1" />
        <rect x="70" y="35" width="7" height="9" rx="2" fill="#f06f8f" stroke="#111" strokeWidth="1" />
        <path d="M46 37h20" stroke="rgba(255,255,255,.55)" strokeWidth="1.2" strokeLinecap="round" />
      </g>
    </svg>
  );
}

interface DeskCardItemProps {
  card: PoolCard;
  checked: boolean;
  onToggle: () => void;
  onDrop: () => void;
  dropDisabled?: boolean;
}

function DeskCardItem({ card, checked, onToggle, onDrop, dropDisabled }: DeskCardItemProps) {
  return (
    <li>
      <label className={`card-checkbox desk-card${checked ? ' checked' : ''}`}>
        <input type="checkbox" checked={checked} onChange={onToggle} />
        <div className="card-checkbox-body">
          <header>
            <strong>{card.title}</strong>
          </header>
          <p className="card-row action-row">
            <span className="row-label cyan">行动</span>
            <span className="row-text">{card.action}</span>
          </p>
          {card.age > 0 && (
            <p className="age-hint muted tiny">
              ※ 这张在桌面上留了 {card.age} 段了,不是债务。
            </p>
          )}
        </div>
        <button
          type="button"
          className="desk-card-drop"
          aria-label={`撕掉「${card.title}」`}
          disabled={dropDisabled}
          onClick={(e) => {
            // The hover button sits inside the <label>; stop the event so toggling
            // the checkbox doesn't fire when the user actually means "tear this card".
            e.preventDefault();
            e.stopPropagation();
            onDrop();
          }}
        >
          撕掉
        </button>
      </label>
    </li>
  );
}

// Re-export onReloadHandle so callers (e.g. App after a new wish writes drafts to local pool)
// can force the Desk to re-read activePoolCards. Currently we re-mount the component instead;
// keeping this hook surface free of refs.
export function deskReloadKey(): number {
  return Date.now();
}
